// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import ReCaptcha from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonProps } from "@insite/mobius/Button";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea from "@insite/mobius/TextArea";
import TextField from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { EmailUsFormState, initailEmailUsFormState } from "@mcmc/App/Components/EmailUsForm/EmailUsForm.helper";
import loadAllLocations from "@mcmc/App/Handlers/AllLocations/LoadAllLocations";
import loadSystemList from "@mcmc/App/Handlers/CommonHandlers/LoadSystemList";
import submitContactUsForm from "@mcmc/App/Handlers/ContactUs/SubmitContactUsForm";
import { initializeScrollTo } from "@mcmc/App/Libraries";
import McMcApplicationState from "@mcmc/App/Store/McMcApplicationState";
import { getSystemList } from "@mcmc/App/Store/Selectors/SystemListSelectors";
import { AForm } from "@mcmc/Overrides/Widgets/CreateAccount/CreateAccount.form-helper";
import EllipsisSpinner from "@mcmc/XDL/Components/Common/EllipsisSpinner";
import { mergeWithCss } from "@mcmc/XDL/Utilities";
import cloneDeep from "lodash/cloneDeep";
import React, { useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const ContactReasonsListName = "ContactReasons";

const mapStateToProps = (state: McMcApplicationState) => {
    const websiteName = state.context.website.name;
    const allLocations = state.xAllLocations.dataView;
    const locationsForSite = allLocations?.locationsCollection.find(s => s.companyName === websiteName)?.locations;

    return {
        allLocations: locationsForSite,
        isLoadingLocations: state.xAllLocations.isLoading,
        contactReasons: getSystemList(state, ContactReasonsListName),
    };
};

const mapDispatchToProps = {
    loadAllLocations,
    submitContactUsForm,
    loadSystemList,
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
};

type Props = HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

type ExtendedWithClassName = { className: string };

export interface XngContactFormStyles {
    container?: InjectableCss & ExtendedWithClassName;
    formRow?: InjectableCss & ExtendedWithClassName;
    formCol?: InjectableCss & ExtendedWithClassName;
    formButtonRow?: InjectableCss & ExtendedWithClassName;
    fieldWrapper?: InjectableCss & ExtendedWithClassName;
    requiredFieldWrapper?: InjectableCss & ExtendedWithClassName;
    pardotField?: InjectableCss & ExtendedWithClassName;
    pardotFieldInValid?: InjectableCss & ExtendedWithClassName;
    submitButton?: ButtonProps;
    formSelect?: SelectPresentationProps;
}

const styles: XngContactFormStyles = {
    container: {
        className: "mktoForm",
    },
    formRow: {
        className: "mktoFormRow",
    },
    formCol: {
        className: "mktoFieldDescriptor",
        css: css`
            && label {
                color: ${getColor("text")};
            }
        `,
    },
    fieldWrapper: {
        className: "mktoFieldWrap",
    },
    requiredFieldWrapper: {
        className: "mktoFieldWrap mktoRequiredField",
    },
    formButtonRow: {
        className: "mktoButtonRow",
    },
    pardotField: {
        className: "mktoField",
    },
    pardotFieldInValid: {
        className: "mktoField mktoInvalid",
    },
    submitButton: {
        className: "mktoButton",
        css: css`
            &&&&&& {
                display: flex;
            }
        `,
    },
    formSelect: {
        cssOverrides: {
            formField: css`
                flex-direction: column;
            `,
            formInputWrapper: css`
                width: 100%;
                margin-top: 5px;
            `,
        },
        css: css`
            margin-top: 0px;
        `,
    },
};

const StyledForm = getStyledWrapper("form");

const EmailUsForm = ({
    loadAllLocations,
    allLocations,
    loadSystemList,
    contactReasons,
    isLoadingLocations,
    submitContactUsForm,
    toaster,
    validateReCaptcha,
}: Props) => {
    const [formState, setFormState] = useState<EmailUsFormState>(cloneDeep(initailEmailUsFormState));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const form = new AForm<EmailUsFormState>(formState, setFormState);
    const { fields, isSubmitted, isValid } = form.state;
    const recaptchaRef: React.RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        if (!allLocations && !isLoadingLocations) {
            loadAllLocations({});
        }
        if (!contactReasons) {
            loadSystemList({ name: ContactReasonsListName });
        }
    }, []);

    useEffect(() => {
        if (form.state.isSubmitted && !form.state.isValid) {
            initializeScrollTo("[data-test-selector*='-error']", -50);
        }
    }, [form.state.isSubmitted]);

    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!form.state.isSubmitted) {
            form.setSubmitted();
        }

        const isReCaptchaValid = await validateReCaptcha({ recaptchaRef, location: "EmailUsForm" });
        if (!isReCaptchaValid) {
            return;
        }
        if (form.validate()) {
            setIsSubmitting(true);
            submitContactUsForm({
                firstName: form.state.fields.firstName.value,
                lastName: form.state.fields.lastName.value,
                emailAddress: form.state.fields.emailAddress.value,
                companyZipCode: form.state.fields.companyZipCode.value,
                companyName: form.state.fields.companyName.value,
                closetBranch: form.state.fields.closetBranch.value,
                whatCanWeHelpYouWith: form.state.fields.whatCanWeHelpYouWith.value,
                phoneNumber: form.state.fields.phoneNumber.value,
                additionalComments: form.state.fields.additionalComments.value,
                onSuccess: () => {
                    toaster.addToast({
                        body: siteMessage("McMc_Contact_Us_Success_Message"),
                        messageType: "success",
                        timeoutLength: 10000,
                    });
                    setIsSubmitting(false);
                    setFormState(cloneDeep(initailEmailUsFormState));
                },
                onError: error => {
                    toaster.addToast({
                        body: error,
                        messageType: "danger",
                        timeoutLength: 10000,
                    });
                    setIsSubmitting(false);
                },
            });
        }
    };

    return (
        <StyledForm id="emailUs_contactForm" onSubmit={submitHandler} noValidate>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.formRow}>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <TextField
                                {...fields.firstName.attrs}
                                value={fields.firstName.value}
                                onChange={e => form.onFieldChange(fields.firstName, e.currentTarget.value)}
                                error={isSubmitted && fields.firstName.error}
                                {...(isSubmitted && fields.firstName.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <TextField
                                {...fields.lastName.attrs}
                                value={fields.lastName.value}
                                onChange={e => form.onFieldChange(fields.lastName, e.currentTarget.value)}
                                error={isSubmitted && fields.lastName.error}
                                {...(isSubmitted && fields.lastName.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.formRow}>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <TextField
                                {...fields.companyName.attrs}
                                value={fields.companyName.value}
                                onChange={e => form.onFieldChange(fields.companyName, e.currentTarget.value)}
                                error={isSubmitted && fields.companyName.error}
                                {...(isSubmitted && fields.companyName.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <TextField
                                {...fields.companyZipCode.attrs}
                                value={fields.companyZipCode.value}
                                onChange={e => form.onFieldChange(fields.companyZipCode, e.currentTarget.value)}
                                error={isSubmitted && fields.companyZipCode.error}
                                {...(isSubmitted && fields.companyZipCode.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.formRow}>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <TextField
                                {...fields.emailAddress.attrs}
                                value={fields.emailAddress.value}
                                onChange={e => form.onFieldChange(fields.emailAddress, e.currentTarget.value)}
                                error={isSubmitted && fields.emailAddress.error}
                                {...(isSubmitted && fields.emailAddress.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.fieldWrapper}>
                            <TextField
                                {...fields.phoneNumber.attrs}
                                value={fields.phoneNumber.value}
                                onChange={e => form.onFieldChange(fields.phoneNumber, e.currentTarget.value)}
                                error={isSubmitted && fields.phoneNumber.error}
                                {...(isSubmitted && fields.phoneNumber.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.formRow}>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <Select
                                labelPosition="left"
                                {...fields.closetBranch.attrs}
                                value={fields.closetBranch.value}
                                onChange={e => form.onFieldChange(fields.closetBranch, e.currentTarget.value)}
                                error={isSubmitted && fields.closetBranch.error}
                                {...(isSubmitted && fields.closetBranch.error
                                    ? mergeWithCss(styles.pardotFieldInValid, styles.formSelect)
                                    : mergeWithCss(styles.pardotField, styles.formSelect))}
                            >
                                <option value="">{translate("Select...")}</option>
                                {allLocations?.map(location => (
                                    <option key={location.name} value={location.warehouseCode}>
                                        {location.name || location.warehouseCode}
                                    </option>
                                ))}
                            </Select>
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.requiredFieldWrapper}>
                            <Select
                                labelPosition="left"
                                {...fields.whatCanWeHelpYouWith.attrs}
                                value={fields.whatCanWeHelpYouWith.value}
                                onChange={e => form.onFieldChange(fields.whatCanWeHelpYouWith, e.currentTarget.value)}
                                error={isSubmitted && fields.whatCanWeHelpYouWith.error}
                                {...(isSubmitted && fields.whatCanWeHelpYouWith.error
                                    ? mergeWithCss(styles.pardotFieldInValid, styles.formSelect)
                                    : mergeWithCss(styles.pardotField, styles.formSelect))}
                            >
                                <option value="">{translate("Select...")}</option>
                                {contactReasons?.values?.map((reason: any) => (
                                    <option key={reason.name} value={reason.name}>
                                        {reason.description || reason.name}
                                    </option>
                                ))}
                            </Select>
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.formRow}>
                    <StyledWrapper {...styles.formCol}>
                        <StyledWrapper {...styles.fieldWrapper}>
                            <TextArea
                                {...fields.additionalComments.attrs}
                                value={fields.additionalComments.value}
                                onChange={e => form.onFieldChange(fields.additionalComments, e.currentTarget.value)}
                                error={isSubmitted && fields.additionalComments.error}
                                {...(isSubmitted && fields.additionalComments.error
                                    ? styles.pardotFieldInValid
                                    : styles.pardotField)}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.formButtonRow}>
                    <Button
                        {...styles.submitButton}
                        data-test-selector="SubmitButton"
                        type="submit"
                        disabled={isSubmitting || (isSubmitted && !isValid)}
                    >
                        <EllipsisSpinner label={translate("Submit")} showSpinner={isSubmitting}></EllipsisSpinner>
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
            <ReCaptcha recaptchaRef={recaptchaRef} location="EmailUsForm" />
        </StyledForm>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(EmailUsForm));
